"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import Link from "next/link";
import FileUploader from "./FileUploader";
import ProcessingOverlay from "./ProcessingOverlay";
import SignupGateModal from "./SignupGateModal";
import UsageLimitBanner from "./UsageLimitBanner";
import { useToolFetch } from "@/hooks/useToolFetch";
import { useAuth } from "@/lib/auth";

/* ─── Color presets ─── */
const COLOR_PRESETS = [
  { hex: "#808080", label: "Gray" },
  { hex: "#dc2626", label: "Red" },
  { hex: "#2563eb", label: "Blue" },
  { hex: "#000000", label: "Black" },
  { hex: "#16a34a", label: "Green" },
] as const;

export default function PdfWatermarkTool() {
  const { user } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [text, setText] = useState("CONFIDENTIAL");
  const [opacity, setOpacity] = useState(0.3);
  const [angle, setAngle] = useState(-45);
  const [fontSize, setFontSize] = useState(60);
  const [color, setColor] = useState("#808080");
  const [position, setPosition] = useState({ x: 0.5, y: 0.5 });
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<{ url: string; size: number } | null>(null);
  const [error, setError] = useState("");
  const [pageDataUrl, setPageDataUrl] = useState<string | null>(null);
  const [pageDims, setPageDims] = useState<{ w: number; h: number } | null>(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [activeHandle, setActiveHandle] = useState<"position" | "resize" | "rotate" | null>(null);
  const { toolFetch, quotaError } = useToolFetch();
  const topRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  /* ─── Drag refs (avoid re-renders during drag) ─── */
  const positionDragging = useRef(false);
  const positionOffset = useRef<{ dx: number; dy: number }>({ dx: 0, dy: 0 });
  const resizeStart = useRef<{ fontSize: number; dist: number } | null>(null);
  const rotateStart = useRef<{ angle: number; pointerAngle: number } | null>(null);

  /* ─── Helper: get center of watermark in client coords ─── */
  const getWatermarkCenter = useCallback(() => {
    const el = previewRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return {
      cx: rect.left + position.x * rect.width,
      cy: rect.top + position.y * rect.height,
      rect,
    };
  }, [position]);

  /* ─── File selection ─── */
  const handleFilesSelected = useCallback((files: File[]) => {
    setFile(files[0] || null);
    setResult(null);
    setError("");
    setPageDataUrl(null);
    setPageDims(null);
  }, []);

  /* ─── Render first page via pdf.js ─── */
  useEffect(() => {
    if (!file) return;
    let cancelled = false;
    setLoadingPdf(true);
    setError("");

    const render = async () => {
      try {
        const pdfjsLib = await import("pdfjs-dist/legacy/build/pdf.mjs");
        pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
          "pdfjs-dist/legacy/build/pdf.worker.min.mjs",
          import.meta.url
        ).toString();
        const data = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data }).promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({
          canvasContext: canvas.getContext("2d")!,
          viewport,
          canvas,
        }).promise;
        if (cancelled) return;
        setPageDataUrl(canvas.toDataURL("image/jpeg", 0.85));
        setPageDims({ w: viewport.width, h: viewport.height });
      } catch {
        if (!cancelled) setError("Failed to load PDF preview");
      } finally {
        if (!cancelled) setLoadingPdf(false);
      }
    };
    render();
    return () => {
      cancelled = true;
    };
  }, [file]);

  /* ═══════════════════════════════════════════
     POSITION DRAG — drag the watermark bounding box
     Uses grab offset so watermark doesn't jump to cursor
     ═══════════════════════════════════════════ */
  const onPositionDown = useCallback(
    (e: React.PointerEvent) => {
      e.stopPropagation();
      e.preventDefault();
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      const el = previewRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // Record offset between pointer and current watermark center
      const wmX = position.x * rect.width;
      const wmY = position.y * rect.height;
      const ptrX = e.clientX - rect.left;
      const ptrY = e.clientY - rect.top;
      positionOffset.current = { dx: ptrX - wmX, dy: ptrY - wmY };
      positionDragging.current = true;
      setActiveHandle("position");
    },
    [position]
  );

  const onPositionMove = useCallback(
    (e: React.PointerEvent) => {
      if (!positionDragging.current) return;
      const el = previewRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const ptrX = e.clientX - rect.left;
      const ptrY = e.clientY - rect.top;
      setPosition({
        x: Math.max(0, Math.min(1, (ptrX - positionOffset.current.dx) / rect.width)),
        y: Math.max(0, Math.min(1, (ptrY - positionOffset.current.dy) / rect.height)),
      });
    },
    []
  );

  const onPositionUp = useCallback(() => {
    positionDragging.current = false;
    setActiveHandle(null);
  }, []);

  /* ═══════════════════════════════════════════
     RESIZE DRAG — corner handles
     ═══════════════════════════════════════════ */
  const onResizeDown = useCallback(
    (e: React.PointerEvent) => {
      e.stopPropagation();
      e.preventDefault();
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      const c = getWatermarkCenter();
      if (!c) return;
      const dx = e.clientX - c.cx;
      const dy = e.clientY - c.cy;
      resizeStart.current = { fontSize, dist: Math.sqrt(dx * dx + dy * dy) };
      setActiveHandle("resize");
    },
    [getWatermarkCenter, fontSize]
  );

  const onResizeMove = useCallback(
    (e: React.PointerEvent) => {
      if (!resizeStart.current) return;
      const c = getWatermarkCenter();
      if (!c) return;
      const dx = e.clientX - c.cx;
      const dy = e.clientY - c.cy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const scale = dist / resizeStart.current.dist;
      const newSize = Math.round(
        Math.max(20, Math.min(120, resizeStart.current.fontSize * scale))
      );
      setFontSize(newSize);
    },
    [getWatermarkCenter]
  );

  const onResizeUp = useCallback(() => {
    resizeStart.current = null;
    setActiveHandle(null);
  }, []);

  /* ═══════════════════════════════════════════
     ROTATE DRAG — top handle
     ═══════════════════════════════════════════ */
  const onRotateDown = useCallback(
    (e: React.PointerEvent) => {
      e.stopPropagation();
      e.preventDefault();
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      const c = getWatermarkCenter();
      if (!c) return;
      const dx = e.clientX - c.cx;
      const dy = e.clientY - c.cy;
      // Angle where 0° = up (12 o'clock), positive = clockwise
      const pointerAngle = Math.atan2(dx, -dy) * (180 / Math.PI);
      rotateStart.current = { angle, pointerAngle };
      setActiveHandle("rotate");
    },
    [getWatermarkCenter, angle]
  );

  const onRotateMove = useCallback(
    (e: React.PointerEvent) => {
      if (!rotateStart.current) return;
      const c = getWatermarkCenter();
      if (!c) return;
      const dx = e.clientX - c.cx;
      const dy = e.clientY - c.cy;
      const pointerAngle = Math.atan2(dx, -dy) * (180 / Math.PI);
      const delta = pointerAngle - rotateStart.current.pointerAngle;
      const newAngle = Math.round(
        Math.max(-90, Math.min(90, rotateStart.current.angle + delta))
      );
      setAngle(newAngle);
    },
    [getWatermarkCenter]
  );

  const onRotateUp = useCallback(() => {
    rotateStart.current = null;
    setActiveHandle(null);
  }, []);

  /* ─── Apply watermark via backend ─── */
  const applyWatermark = useCallback(async () => {
    if (!file || !text.trim()) return;
    setProcessing(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("text", text);
      formData.append("opacity", String(opacity));
      formData.append("angle", String(angle));
      formData.append("font_size", String(fontSize));
      formData.append("color", color);
      formData.append("x_position", String(position.x));
      formData.append("y_position", String(position.y));

      const res = await toolFetch("/api/tools/watermark", {
        method: "POST",
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to add watermark");
      }

      const blob = await res.blob();
      setResult({ url: URL.createObjectURL(blob), size: blob.size });
      setTimeout(() => topRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e: any) {
      setError(e.message || "Failed to add watermark");
    } finally {
      setProcessing(false);
    }
  }, [file, text, opacity, angle, fontSize, color, position]);

  /* ─── Reset ─── */
  const reset = useCallback(() => {
    setFile(null);
    setResult(null);
    setError("");
    setPageDataUrl(null);
    setPageDims(null);
    setText("CONFIDENTIAL");
    setOpacity(0.3);
    setAngle(-45);
    setFontSize(60);
    setColor("#808080");
    setPosition({ x: 0.5, y: 0.5 });
  }, []);

  /* ─── Format file size ─── */
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  /* ─── Signup gate ─── */
  if (quotaError?.gate === "signup") return <SignupGateModal quotaError={quotaError} />;

  /* ─── Shared handle styles — hidden until hover/active for clean WYSIWYG ─── */
  const cornerCls = `absolute w-4 h-4 rounded-full bg-white border-2 border-blue-500 shadow-sm pointer-events-auto z-10 hover:scale-125 transition-all duration-150 ${
    activeHandle
      ? "opacity-100"
      : "opacity-0 group-hover/wm:opacity-100"
  }`;

  /* ═══════════════════════════════════════════
     RESULT SCREEN
     ═══════════════════════════════════════════ */
  if (result) {
    return (
      <div ref={topRef} className="space-y-8">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 mb-4">
            <svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-1">Your watermarked PDF is ready</h3>
          <p className="text-sm text-slate-500 mb-5">watermarked.pdf ({formatSize(result.size)})</p>
          <div className="flex items-center justify-center gap-3">
            <a href={result.url} download="watermarked.pdf" className="btn-primary px-8 py-3 inline-flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download
            </a>
            <button onClick={reset} className="btn px-6 py-3 border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg font-semibold text-sm">
              Process another
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Link href={user ? "/dashboard/locker" : "/register"} className="group block border border-slate-200 rounded-xl p-5 hover:border-[var(--color-gold)] hover:shadow-md transition-all">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div>
                <h4 className="font-bold text-slate-900 group-hover:text-[var(--color-gold)] transition-colors">Store Securely</h4>
                <p className="text-sm text-slate-500 mt-1">Keep your watermarked document safe in SignForge&apos;s secure vault. Access it anytime.</p>
              </div>
            </div>
          </Link>
          <Link href={user ? "/dashboard/envelopes/new" : "/register"} className="group block border border-slate-200 rounded-xl p-5 hover:border-[var(--color-gold)] hover:shadow-md transition-all">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center">
                <svg className="w-5 h-5 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </div>
              <div>
                <h4 className="font-bold text-slate-900 group-hover:text-[var(--color-gold)] transition-colors">Send for Signature</h4>
                <p className="text-sm text-slate-500 mt-1">Need someone to sign this document? Send it for e-signature in seconds.</p>
              </div>
            </div>
          </Link>
        </div>

        {!user && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl px-6 py-4 flex flex-col sm:flex-row items-center gap-4">
            <div className="flex-1 text-center sm:text-left">
              <p className="text-sm text-slate-600">Create a free account &mdash; 50 tools/day, 10 e-signature envelopes/mo, secure document vault.</p>
            </div>
            <Link href="/register" className="whitespace-nowrap text-sm font-bold text-[var(--color-gold)] hover:text-[var(--color-gold-light)] transition-colors">
              Sign up free &rarr;
            </Link>
          </div>
        )}
      </div>
    );
  }

  /* ═══════════════════════════════════════════
     EDITOR UI
     ═══════════════════════════════════════════ */
  return (
    <div ref={topRef} className="relative space-y-6">
      {processing && <ProcessingOverlay message="Applying watermark..." />}
      {quotaError?.gate === "limit" && <UsageLimitBanner quotaError={quotaError} />}

      {/* Upload phase */}
      {!file && (
        <FileUploader
          accept=".pdf"
          onFilesSelected={handleFilesSelected}
          label="Drop your PDF here"
          description="Add a text watermark to every page"
        />
      )}

      {/* Editor phase */}
      {file && !result && (
        <div className="space-y-6">
          {/* PDF Preview with interactive watermark */}
          <div className="bg-slate-100 rounded-xl border border-slate-200 overflow-hidden">
            {loadingPdf && (
              <div className="flex items-center justify-center py-24">
                <div className="flex items-center gap-3 text-slate-500">
                  <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Loading preview...
                </div>
              </div>
            )}

            {pageDataUrl && pageDims && (
              <div
                ref={previewRef}
                className="relative mx-auto select-none"
                style={{ maxWidth: 680 }}
              >
                {/* PDF page */}
                <img
                  src={pageDataUrl}
                  alt="PDF page 1"
                  className="w-full h-auto block pointer-events-none"
                  draggable={false}
                />

                {/* ─── Interactive watermark group ─── */}
                <div
                  className="absolute group/wm"
                  style={{
                    left: `${position.x * 100}%`,
                    top: `${position.y * 100}%`,
                    transform: `translate(-50%, -50%) rotate(${angle}deg)`,
                  }}
                >
                  {/* Rotation handle — above the bounding box, hidden until hover/active */}
                  <div
                    className={`absolute left-1/2 -translate-x-1/2 flex flex-col items-center pointer-events-auto z-10 transition-opacity duration-150 ${
                      activeHandle
                        ? "opacity-100"
                        : "opacity-0 group-hover/wm:opacity-100"
                    }`}
                    style={{ bottom: "100%", marginBottom: 0, cursor: activeHandle === "rotate" ? "grabbing" : "grab", touchAction: "none" }}
                    onPointerDown={onRotateDown}
                    onPointerMove={onRotateMove}
                    onPointerUp={onRotateUp}
                    onPointerCancel={onRotateUp}
                  >
                    <div className="w-6 h-6 rounded-full bg-white border-2 border-blue-500 shadow-sm flex items-center justify-center hover:bg-blue-50 transition-colors">
                      <svg className="w-3.5 h-3.5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                    <div className="w-px h-3 bg-blue-400" />
                  </div>

                  {/* Bounding box — drag to reposition. Border hidden by default for clean WYSIWYG preview */}
                  <div
                    className={`relative border-2 border-dashed rounded-sm px-3 py-1 pointer-events-auto transition-colors duration-150 ${
                      activeHandle
                        ? "border-blue-400/50"
                        : "border-transparent hover:border-blue-400/40"
                    }`}
                    style={{
                      minWidth: 32,
                      minHeight: 20,
                      cursor: activeHandle === "position" ? "grabbing" : "grab",
                      touchAction: "none",
                    }}
                    onPointerDown={onPositionDown}
                    onPointerMove={onPositionMove}
                    onPointerUp={onPositionUp}
                    onPointerCancel={onPositionUp}
                  >
                    {/* Watermark text */}
                    <span
                      className="block whitespace-nowrap"
                      style={{
                        opacity: opacity,
                        color: color,
                        fontSize: `${fontSize * (680 / pageDims.w)}px`,
                        fontFamily: "Helvetica, Arial, sans-serif",
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                        lineHeight: 1.2,
                        textTransform: "uppercase",
                      }}
                    >
                      {text || "\u00A0"}
                    </span>

                    {/* Corner resize handles */}
                    <div
                      className={cornerCls}
                      style={{ top: -8, left: -8, cursor: activeHandle === "resize" ? "grabbing" : "nwse-resize", touchAction: "none" }}
                      onPointerDown={onResizeDown}
                      onPointerMove={onResizeMove}
                      onPointerUp={onResizeUp}
                      onPointerCancel={onResizeUp}
                    />
                    <div
                      className={cornerCls}
                      style={{ top: -8, right: -8, cursor: activeHandle === "resize" ? "grabbing" : "nesw-resize", touchAction: "none" }}
                      onPointerDown={onResizeDown}
                      onPointerMove={onResizeMove}
                      onPointerUp={onResizeUp}
                      onPointerCancel={onResizeUp}
                    />
                    <div
                      className={cornerCls}
                      style={{ bottom: -8, left: -8, cursor: activeHandle === "resize" ? "grabbing" : "nesw-resize", touchAction: "none" }}
                      onPointerDown={onResizeDown}
                      onPointerMove={onResizeMove}
                      onPointerUp={onResizeUp}
                      onPointerCancel={onResizeUp}
                    />
                    <div
                      className={cornerCls}
                      style={{ bottom: -8, right: -8, cursor: activeHandle === "resize" ? "grabbing" : "nwse-resize", touchAction: "none" }}
                      onPointerDown={onResizeDown}
                      onPointerMove={onResizeMove}
                      onPointerUp={onResizeUp}
                      onPointerCancel={onResizeUp}
                    />
                  </div>
                </div>

                {/* Badges */}
                <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded pointer-events-none">
                  Page 1 preview
                </div>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full pointer-events-none flex items-center gap-1.5">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                  </svg>
                  Drag watermark to move · corners to resize · top to rotate
                </div>
              </div>
            )}
          </div>

          {/* Controls panel */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-5">
            {/* Watermark text */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Watermark text</label>
              <input
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="CONFIDENTIAL"
                className="input-field"
                maxLength={100}
              />
            </div>

            {/* Font size slider */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-semibold text-slate-700">Font size</label>
                <span className="text-sm font-mono text-slate-500 tabular-nums">{fontSize}pt</span>
              </div>
              <input
                type="range"
                min={20}
                max={120}
                value={fontSize}
                onChange={(e) => setFontSize(Number(e.target.value))}
                className="w-full accent-[var(--color-gold)] h-2"
              />
              <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
                <span>20pt</span>
                <span>120pt</span>
              </div>
            </div>

            {/* Opacity slider */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-semibold text-slate-700">Opacity</label>
                <span className="text-sm font-mono text-slate-500 tabular-nums">{Math.round(opacity * 100)}%</span>
              </div>
              <input
                type="range"
                min={5}
                max={100}
                value={opacity * 100}
                onChange={(e) => setOpacity(Number(e.target.value) / 100)}
                className="w-full accent-[var(--color-gold)] h-2"
              />
              <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
                <span>5%</span>
                <span>100%</span>
              </div>
            </div>

            {/* Angle slider */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-semibold text-slate-700">Angle</label>
                <span className="text-sm font-mono text-slate-500 tabular-nums">{angle}°</span>
              </div>
              <input
                type="range"
                min={-90}
                max={90}
                value={angle}
                onChange={(e) => setAngle(Number(e.target.value))}
                className="w-full accent-[var(--color-gold)] h-2"
              />
              <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
                <span>-90°</span>
                <span>0°</span>
                <span>90°</span>
              </div>
            </div>

            {/* Color presets */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Color</label>
              <div className="flex items-center gap-3">
                {COLOR_PRESETS.map((preset) => (
                  <button
                    key={preset.hex}
                    onClick={() => setColor(preset.hex)}
                    title={preset.label}
                    className="relative w-9 h-9 rounded-full border-2 transition-all duration-150 focus:outline-none"
                    style={{
                      backgroundColor: preset.hex,
                      borderColor: color === preset.hex ? "var(--color-gold)" : "transparent",
                      boxShadow: color === preset.hex ? "0 0 0 2px var(--color-gold-muted)" : "none",
                    }}
                  >
                    {color === preset.hex && (
                      <svg className="absolute inset-0 m-auto w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="#fff" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    <span className="sr-only">{preset.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Error */}
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{error}</p>
          )}

          {/* Action buttons */}
          <div className="flex gap-3">
            <button
              onClick={applyWatermark}
              disabled={processing || !text.trim()}
              className="btn-primary flex-1 py-3 flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Apply Watermark &amp; Download
            </button>
            <button
              onClick={reset}
              className="btn px-4 py-3 border border-slate-300 text-slate-600 hover:bg-slate-50 rounded-lg text-sm font-medium"
              title="Start over"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
